import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Search, X, SlidersHorizontal } from 'lucide-react';
import { Product, Branch, Category } from '../types';
import ProductCard from './ProductCard';
import BrandLogo from './BrandLogo';
import { POPULAR_BRANDS, PRODUCT_CATEGORIES } from '../config/siteConfig';
import { includesNormalized } from '../utils/textUtils';

interface BrandCatalogProps {
    brandName: string;
    products: Product[];
    currentBranch: Branch;
    onBack: () => void;
    isLoading?: boolean;
    initialModel?: string | null;
    onProductSelect?: (product: Product) => void;
    onSearch?: (query: string) => void; // New prop for global search trigger
    isSearchMode?: boolean;
    initialFilters?: {
        magsafe?: boolean;
        forged?: boolean;
        carbon?: boolean;
    };
}

const BrandCatalog: React.FC<BrandCatalogProps> = ({
    brandName,
    products,
    currentBranch,
    onBack,
    isLoading = false,
    initialModel = null,
    onProductSelect,
    onSearch,
    isSearchMode = false,
    initialFilters = {}
}) => {
    const [searchTerm, setSearchTerm] = useState(isSearchMode ? brandName : '');
    const [inputValue, setInputValue] = useState(isSearchMode ? brandName : '');

    const handleSearch = () => {
        if (isSearchMode && onSearch && inputValue.trim()) {
            onSearch(inputValue);
        } else {
            setSearchTerm(inputValue);
            setCurrentPage(1);
        }
    };
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const [selectedModel, setSelectedModel] = useState<string | null>(initialModel);
    const [selectedBrandFilter, setSelectedBrandFilter] = useState<string | null>(null);

    // New Filters
    const [filterMagsafe, setFilterMagsafe] = useState(initialFilters.magsafe || false);
    const [filterForged, setFilterForged] = useState(initialFilters.forged || false);
    const [filterCarbon, setFilterCarbon] = useState(initialFilters.carbon || false);

    const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 20;

    // Update selected model if initialModel changes
    useEffect(() => {
        if (initialModel) setSelectedModel(initialModel);
    }, [initialModel]);

    // Get brand info for logo/models
    const brandInfo = POPULAR_BRANDS.find(b => b.name === brandName);

    // Filter Logic
    let filteredProducts = products.filter(product => {
        // Brand check - SKIP if in Search Mode
        if (!isSearchMode) {
            // We trust the incoming list.
        }

        // Search Term (Accent-insensitive)
        if (searchTerm &&
            !includesNormalized(product.name, searchTerm) &&
            !product.compatibleModels.some(m => includesNormalized(m, searchTerm))) {
            return false;
        }

        // Brand Filter (New for Global Catalog)
        if (selectedBrandFilter && product.make !== selectedBrandFilter) return false;

        // Category - Handle legacy 'Fundas' mapping
        if (selectedCategory) {
            const isCaseMatch = selectedCategory === 'Case' && (product.category === 'Case' || product.category === 'Fundas');
            if (!isCaseMatch && product.category !== selectedCategory) return false;
        }

        // Model
        if (selectedModel && !product.compatibleModels.some(m => m.includes(selectedModel))) return false;

        // Magsafe Filter
        if (filterMagsafe && !includesNormalized(product.name, 'magsafe')) return false;

        // Material Filters (OR Logic between materials if both selected)
        const isForgedItem = includesNormalized(product.name, 'forjado') || includesNormalized(product.name, 'forjada');
        const isCarbonItem = !isForgedItem; // Assuming everything else is standard carbon

        // If any material filter is active, check if item matches at least one active material
        if (filterForged || filterCarbon) {
            let match = false;
            if (filterForged && isForgedItem) match = true;
            if (filterCarbon && isCarbonItem) match = true;
            if (!match) return false;
        }

        return true;
    });

    // Reset page on filter change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, selectedCategory, selectedModel, selectedBrandFilter, filterMagsafe, filterForged, filterCarbon, brandName]);

    const totalPages = Math.ceil(filteredProducts.length / pageSize);
    const paginatedProducts = filteredProducts.slice((currentPage - 1) * pageSize, currentPage * pageSize);


    // Scroll to top on mount
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const clearFilters = () => {
        setSearchTerm('');
        setInputValue('');
        setSelectedCategory(null);
        setSelectedModel(null);
        setSelectedBrandFilter(null);
        setFilterMagsafe(false);
        setFilterForged(false);
        setFilterCarbon(false);
    };

    const toggleFilter = (setter: React.Dispatch<React.SetStateAction<boolean>>) => {
        setter(prev => !prev);
    };

    return (
        <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="bg-zinc-950 min-h-screen relative z-0"
        >
            {/* Catalog Header */}
            <div className="bg-black border-b border-zinc-800 pt-8 pb-12 px-4 sm:px-6 lg:px-8 relative z-40">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 blur-[100px] rounded-full pointer-events-none" />

                <div className="max-w-7xl mx-auto relative z-10">
                    {/* Header Top Row: Back Auth & Breadcrumbs */}
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                        <button
                            onClick={onBack}
                            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors group w-fit"
                        >
                            <ChevronLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                            <span className="font-display uppercase tracking-widest text-sm font-bold">Volver a Marcas</span>
                        </button>

                        {/* Breadcrumbs */}
                        <div className="flex items-center gap-2 text-sm font-light text-gray-500 overflow-x-auto whitespace-nowrap scrollbar-hide">
                            <span className="opacity-60">Inicio</span>
                            <ChevronRight className="w-3 h-3 text-zinc-700" />
                            {isSearchMode ? (
                                <>
                                    <span className="opacity-60">Búsqueda</span>
                                    <ChevronRight className="w-3 h-3 text-zinc-700" />
                                    <span className={`${!selectedCategory && !selectedModel ? 'text-white font-bold' : 'text-gray-300'}`}>"{brandName}"</span>
                                </>
                            ) : (
                                <span className={`${!selectedCategory && !selectedModel ? 'text-white font-bold' : 'text-gray-300'}`}>{brandName}</span>
                            )}
                            {selectedModel && (
                                <>
                                    <ChevronRight className="w-3 h-3 text-zinc-700" />
                                    <span className="text-gray-300">{selectedModel}</span>
                                </>
                            )}
                            {selectedCategory && (
                                <>
                                    <ChevronRight className="w-3 h-3 text-zinc-700" />
                                    <span className="text-white font-bold">{selectedCategory}</span>
                                </>
                            )}
                        </div>
                    </div>

                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                        <div className="flex items-center gap-6">
                            {!isSearchMode && brandInfo && (
                                <div className="w-16 h-16 md:w-24 md:h-24 bg-white/5 p-3 md:p-4 rounded border border-white/10 backdrop-blur-sm relative z-50">
                                    <BrandLogo brandName={brandName} className="w-full h-full" noFilter={true} />
                                </div>
                            )}
                            <div>
                                <h1 className="text-4xl md:text-6xl font-display font-black text-white uppercase italic tracking-tighter">
                                    {isSearchMode ? 'Resultados' : brandName === 'Catálogo' ? 'Catálogo Completo' : brandName}
                                </h1>
                                <p className="text-white font-mono text-sm uppercase tracking-widest mt-1">
                                    {filteredProducts.length} Productos encontrados
                                </p>
                            </div>
                        </div>

                        {/* Search Bar */}
                        <div className="w-full md:w-auto flex flex-col sm:flex-row gap-2">
                            <div className="w-full md:w-80 relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Search className="h-5 w-5 text-gray-500" />
                                </div>
                                <input
                                    type="text"
                                    className="w-full bg-zinc-900/80 border border-zinc-700 text-white text-sm rounded-none focus:ring-white focus:border-white block pl-10 p-3 placeholder-gray-500 font-sans"
                                    placeholder={isSearchMode ? "Nueva búsqueda..." : `Buscar en ${brandName}...`}
                                    value={inputValue}
                                    onChange={(e) => setInputValue(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                                />
                            </div>
                            <button
                                onClick={handleSearch}
                                className="bg-white hover:bg-zinc-200 text-black font-bold uppercase text-sm px-6 py-3 transition-colors flex items-center gap-2"
                            >
                                <Search className="w-4 h-4" />
                                BUSCAR
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex flex-col lg:flex-row gap-8">

                    {/* Mobile Filter Toggle */}
                    <button
                        className="lg:hidden w-full bg-zinc-900 border border-zinc-700 p-3 flex items-center justify-center gap-2 text-white font-bold uppercase"
                        onClick={() => setIsMobileFilterOpen(true)}
                    >
                        <SlidersHorizontal className="w-4 h-4" /> Filtros
                    </button>

                    {/* Sidebar Filters (Desktop + Mobile Drawer) */}
                    <div className={`
            fixed inset-0 z-50 bg-black/95 backdrop-blur-xl p-6 lg:p-0 lg:bg-transparent lg:static lg:w-64 lg:block overflow-y-auto lg:backdrop-blur-none
            ${isMobileFilterOpen ? 'block' : 'hidden'}
          `}>
                        <div className="flex justify-between items-center lg:hidden mb-6">
                            <h2 className="text-xl font-display font-bold uppercase text-white">Filtros</h2>
                            <button onClick={() => setIsMobileFilterOpen(false)} className="text-gray-400">
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="space-y-8 lg:sticky lg:top-20 lg:z-10 lg:bg-zinc-950/80 lg:backdrop-blur-md lg:p-6 lg:rounded lg:border lg:border-zinc-800">
                            {/* Brand Filter (Only for Catalog View) */}
                            {brandName === 'Catálogo' && (
                                <div>
                                    <h3 className="text-white font-display font-bold uppercase tracking-wider mb-4 text-sm">Marca</h3>
                                    <div className="space-y-2">
                                        <button
                                            onClick={() => setSelectedBrandFilter(null)}
                                            className={`block text-sm text-left w-full hover:text-white transition-colors ${selectedBrandFilter === null ? 'text-white font-bold' : 'text-gray-500'}`}
                                        >
                                            Todas las Marcas
                                        </button>
                                        {POPULAR_BRANDS.filter(b => b.name !== 'Accesorios').map(brand => (
                                            <button
                                                key={brand.name}
                                                onClick={() => setSelectedBrandFilter(brand.name)}
                                                className={`block text-sm text-left w-full hover:text-white transition-colors ${selectedBrandFilter === brand.name ? 'text-white font-bold pl-2 border-l-2 border-white' : 'text-gray-500'}`}
                                            >
                                                {brand.name}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Category Filter */}
                            <div>
                                <h3 className="text-white font-display font-bold uppercase tracking-wider mb-4 text-sm flex items-center gap-2">
                                    <SlidersHorizontal className="w-4 h-4" /> Sistema
                                </h3>
                                <div className="space-y-2">
                                    <label className="flex items-center gap-3 cursor-pointer group">
                                        <div className={`w-4 h-4 border flex items-center justify-center ${selectedCategory === null ? 'border-white bg-white' : 'border-zinc-700 group-hover:border-white'}`}>
                                            {selectedCategory === null && <div className="w-2 h-2 bg-black" />}
                                        </div>
                                        <input
                                            type="radio"
                                            name="category"
                                            className="hidden"
                                            checked={selectedCategory === null}
                                            onChange={() => setSelectedCategory(null)}
                                        />
                                        <span className={`text-sm ${selectedCategory === null ? 'text-white' : 'text-gray-400 group-hover:text-white'}`}>Todos</span>
                                    </label>
                                    {PRODUCT_CATEGORIES.map(cat => (
                                        <label key={cat} className="flex items-center gap-3 cursor-pointer group">
                                            <div className={`w-4 h-4 border flex items-center justify-center ${selectedCategory === cat ? 'border-white bg-white' : 'border-zinc-700 group-hover:border-white'}`}>
                                                {selectedCategory === cat && <div className="w-2 h-2 bg-black" />}
                                            </div>
                                            <input
                                                type="radio"
                                                name="category"
                                                className="hidden"
                                                checked={selectedCategory === cat}
                                                onChange={() => setSelectedCategory(cat)}
                                            />
                                            <span className={`text-sm ${selectedCategory === cat ? 'text-white' : 'text-gray-400 group-hover:text-white'}`}>{cat}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Models Filter */}
                            {brandInfo && (
                                <div>
                                    <h3 className="text-white font-display font-bold uppercase tracking-wider mb-4 text-sm">Modelo</h3>
                                    <div className="space-y-2">
                                        <button
                                            onClick={() => setSelectedModel(null)}
                                            className={`block text-sm text-left w-full hover:text-white transition-colors ${selectedModel === null ? 'text-white font-bold' : 'text-gray-500'}`}
                                        >
                                            Todos
                                        </button>
                                        {brandInfo.popularModels.map(model => (
                                            <button
                                                key={model}
                                                onClick={() => setSelectedModel(model)}
                                                className={`block text-sm text-left w-full hover:text-white transition-colors ${selectedModel === model ? 'text-white font-bold pl-2 border-l-2 border-white' : 'text-gray-500'}`}
                                            >
                                                {model}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Type & Feature Filters (Replaces Estado) */}
                            <div className="space-y-4">
                                <div>
                                    <h3 className="text-white font-display font-bold uppercase tracking-wider mb-4 text-sm">Características</h3>

                                    {/* Magsafe Toggle */}
                                    <label className="flex items-center gap-3 cursor-pointer group p-3 border border-zinc-800 rounded bg-zinc-900/50 hover:border-zinc-600 mb-3 transition-colors">
                                        <div className={`w-5 h-5 border rounded flex items-center justify-center transition-colors ${filterMagsafe ? 'border-white bg-white text-black' : 'border-zinc-600 text-transparent'}`}>
                                            <div className="w-2.5 h-2.5 bg-black rounded-sm" />
                                        </div>
                                        <input
                                            type="checkbox"
                                            className="hidden"
                                            checked={filterMagsafe}
                                            onChange={(e) => setFilterMagsafe(e.target.checked)}
                                        />
                                        <span className="text-sm font-bold text-white">MagSafe ⚡</span>
                                    </label>

                                    {/* Forged Toggle */}
                                    <label className="flex items-center gap-3 cursor-pointer group p-3 border border-zinc-800 rounded bg-zinc-900/50 hover:border-zinc-600 mb-3 transition-colors">
                                        <div className={`w-5 h-5 border rounded flex items-center justify-center transition-colors ${filterForged ? 'border-white bg-white text-black' : 'border-zinc-600 text-transparent'}`}>
                                            <div className="w-2.5 h-2.5 bg-black rounded-sm" />
                                        </div>
                                        <input
                                            type="checkbox"
                                            className="hidden"
                                            checked={filterForged}
                                            onChange={(e) => setFilterForged(e.target.checked)}
                                        />
                                        <span className="text-sm font-bold text-white">Fibra Forjada</span>
                                    </label>

                                    {/* Carbon (Normal) Toggle */}
                                    <label className="flex items-center gap-3 cursor-pointer group p-3 border border-zinc-800 rounded bg-zinc-900/50 hover:border-zinc-600 transition-colors">
                                        <div className={`w-5 h-5 border rounded flex items-center justify-center transition-colors ${filterCarbon ? 'border-white bg-white text-black' : 'border-zinc-600 text-transparent'}`}>
                                            <div className="w-2.5 h-2.5 bg-black rounded-sm" />
                                        </div>
                                        <input
                                            type="checkbox"
                                            className="hidden"
                                            checked={filterCarbon}
                                            onChange={(e) => setFilterCarbon(e.target.checked)}
                                        />
                                        <span className="text-sm font-bold text-white">Fibra de Carbono</span>
                                    </label>
                                </div>
                            </div>

                            {/* Mobile Button to Close */}
                            <button
                                className="lg:hidden w-full bg-white text-black font-bold uppercase py-3 mt-8"
                                onClick={() => setIsMobileFilterOpen(false)}
                            >
                                Ver {filteredProducts.length} Resultados
                            </button>
                        </div>
                    </div>

                    {/* Product Grid */}
                    <div className="flex-1">
                        {/* Quick Filter Pills (Optional Shortcuts) */}
                        <div className="flex flex-wrap gap-2 mb-8">
                            <button
                                onClick={clearFilters}
                                className={`px-6 py-2 text-[10px] font-black uppercase tracking-widest border transition-all ${!filterMagsafe && !filterForged && !filterCarbon
                                    ? 'bg-white border-white text-black shadow-[0_0_15px_rgba(255,255,255,0.3)]'
                                    : 'bg-transparent border-zinc-800 text-zinc-500 hover:border-zinc-600 hover:text-white'
                                    }`}
                            >
                                Todos
                            </button>
                            <button
                                onClick={() => toggleFilter(setFilterMagsafe)}
                                className={`px-6 py-2 text-[10px] font-black uppercase tracking-widest border transition-all ${filterMagsafe
                                    ? 'bg-white border-white text-black shadow-[0_0_15px_rgba(255,255,255,0.3)]'
                                    : 'bg-transparent border-zinc-800 text-zinc-500 hover:border-zinc-600 hover:text-white'
                                    }`}
                            >
                                MagSafe ⚡
                            </button>
                            <button
                                onClick={() => toggleFilter(setFilterForged)}
                                className={`px-6 py-2 text-[10px] font-black uppercase tracking-widest border transition-all ${filterForged
                                    ? 'bg-white border-white text-black shadow-[0_0_15px_rgba(255,255,255,0.3)]'
                                    : 'bg-transparent border-zinc-800 text-zinc-500 hover:border-zinc-600 hover:text-white'
                                    }`}
                            >
                                Fibra Forjada
                            </button>
                            <button
                                onClick={() => toggleFilter(setFilterCarbon)}
                                className={`px-6 py-2 text-[10px] font-black uppercase tracking-widest border transition-all ${filterCarbon
                                    ? 'bg-white border-white text-black shadow-[0_0_15px_rgba(255,255,255,0.3)]'
                                    : 'bg-transparent border-zinc-800 text-zinc-500 hover:border-zinc-600 hover:text-white'
                                    }`}
                            >
                                Fibra de Carbono
                            </button>
                        </div>

                        {filteredProducts.length > 0 ? (
                            <>
                                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
                                    {paginatedProducts.map(product => (
                                        <ProductCard
                                            key={product.id}
                                            product={product}
                                            currentBranch={currentBranch}
                                            onProductClick={onProductSelect}
                                        />
                                    ))}
                                </div>

                                {/* Pagination Controls */}
                                {totalPages > 1 && (
                                    <div className="mt-12 flex flex-col items-center gap-6">
                                        <div className="flex items-center gap-1.5">
                                            <button
                                                onClick={() => {
                                                    setCurrentPage(prev => Math.max(prev - 1, 1));
                                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                                }}
                                                disabled={currentPage === 1}
                                                className={`p-2 border border-zinc-800 transition-all ${currentPage === 1 ? 'opacity-20 cursor-not-allowed' : 'hover:border-white text-white hover:bg-white/10 active:scale-95'}`}
                                            >
                                                <ChevronLeft className="w-5 h-5" />
                                            </button>

                                            <div className="flex items-center gap-1">
                                                {/* Logic to show limited page numbers for cleaner UI */}
                                                {(() => {
                                                    const pages = [];
                                                    const delta = 1; // Number of pages to show around current page
                                                    let lastPage = 0;

                                                    for (let i = 1; i <= totalPages; i++) {
                                                        if (
                                                            i === 1 ||
                                                            i === totalPages ||
                                                            (i >= currentPage - delta && i <= currentPage + delta)
                                                        ) {
                                                            if (lastPage && i - lastPage > 1) {
                                                                pages.push(<span key={`dots-${i}`} className="text-zinc-700 px-1">...</span>);
                                                            }
                                                            pages.push(
                                                                <button
                                                                    key={i}
                                                                    onClick={() => {
                                                                        setCurrentPage(i);
                                                                        window.scrollTo({ top: 0, behavior: 'smooth' });
                                                                    }}
                                                                    className={`w-10 h-10 border transition-all font-display font-bold text-xs ${currentPage === i ? 'bg-white text-black border-white shadow-[0_0_15px_rgba(255,255,255,0.4)]' : 'border-zinc-800 text-gray-500 hover:border-zinc-600 hover:text-white active:scale-95'}`}
                                                                >
                                                                    {i}
                                                                </button>
                                                            );
                                                            lastPage = i;
                                                        }
                                                    }
                                                    return pages;
                                                })()}
                                            </div>

                                            <button
                                                onClick={() => {
                                                    setCurrentPage(prev => Math.min(prev + 1, totalPages));
                                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                                }}
                                                disabled={currentPage === totalPages}
                                                className={`p-2 border border-zinc-800 transition-all ${currentPage === totalPages ? 'opacity-20 cursor-not-allowed' : 'hover:border-white text-white hover:bg-white/10 active:scale-95'}`}
                                            >
                                                <ChevronRight className="w-5 h-5" />
                                            </button>
                                        </div>
                                        <div className="text-[10px] font-mono text-zinc-600 uppercase tracking-widest">
                                            Página {currentPage} de {totalPages} • Total {filteredProducts.length} artículos
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="h-64 flex flex-col items-center justify-center text-center border border-dashed border-zinc-800 rounded">
                                <p className="text-gray-500 mb-4">No se encontraron piezas con estos filtros.</p>
                                <button
                                    onClick={clearFilters}
                                    className="text-white font-bold uppercase text-sm hover:underline"
                                >
                                    Limpiar Filtros
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

export default BrandCatalog;
